// Reusable weekly LH grid editor: 7 rows (Saturday-first) of loads / trailer /
// arrival times for one lane. Pure presentational — parent owns the value and
// validates on save via buildWeekDays().

import { Input, Select } from "../../components/ui";
import type { WeekDayDraft } from "./lhScheduleApi";

// Index = day_of_week as stored (0 = Mon .. 6 = Sun).
export const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// Display order: Saturday first, Friday last.
const DISPLAY_ORDER = [5, 6, 0, 1, 2, 3, 4];

const TRAILER_OPTIONS = [
  { id: "", label: "—" },
  { id: "53ft", label: "53 ft trailer" },
  { id: "box_truck", label: "Box truck" },
];

/** A blank 7-day week (index = day_of_week). */
export function emptyWeek(): WeekDayDraft[] {
  return DAY_NAMES.map(() => ({ loads: "0", trailerType: "", arrivalTimes: "" }));
}

interface Props {
  value: WeekDayDraft[];
  onChange: (next: WeekDayDraft[]) => void;
}

export default function LhWeekEditor({ value, onChange }: Props) {
  function updateDay(i: number, patch: Partial<WeekDayDraft>) {
    onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
          <th className="py-1 font-medium">Day</th>
          <th className="py-1 font-medium">Loads</th>
          <th className="py-1 font-medium">Trailer</th>
          <th className="py-1 font-medium">Arrival times (HH:MM, comma-separated)</th>
        </tr>
      </thead>
      <tbody>
        {DISPLAY_ORDER.map((dow) => (
          <tr key={dow}>
            <td className="py-1 pr-3 font-medium text-slate-700">{DAY_NAMES[dow]}</td>
            <td className="w-20 py-1 pr-2">
              <Input
                type="number"
                min={0}
                value={value[dow].loads}
                onChange={(e) => updateDay(dow, { loads: e.target.value })}
              />
            </td>
            <td className="w-36 py-1 pr-2">
              <Select
                value={value[dow].trailerType}
                onChange={(e) => updateDay(dow, { trailerType: e.target.value })}
              >
                {TRAILER_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </td>
            <td className="py-1">
              <Input
                value={value[dow].arrivalTimes}
                onChange={(e) => updateDay(dow, { arrivalTimes: e.target.value })}
                placeholder="e.g. 08:00, 14:30"
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
